import {
  Button,
  FormField,
  FormFieldError,
  FormFieldLabel,
  Input,
  NativeSelect,
  RichTextEditor,
} from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { BlockedEditTooltip } from '@/features/jobs/job-shared/BlockedEditTooltip';
import { JobHeaderLogo } from '@/features/jobs/job-shared/JobHeaderLogo';

export interface JobEditInfoValues {
  name: string;
  programId: string;
  description: string;
  enrollmentGoal: string;
}

export interface JobEditInfoProgramOption {
  value: string;
  label: string;
}

export interface JobEditInfoPageProps {
  jobName: string;
  companyLogoUrl?: string | null;
  companyName?: string | null;
  values: JobEditInfoValues;
  errors: Record<string, string>;
  isSubmitting: boolean;
  /**
   * EU-569 — vaga Encerrada / Cancelada: modo somente visualização. Fieldset
   * nativo desabilita os campos, cada um ganha o envelope "Edição bloqueada"
   * e o rodapé troca o submit por "Salvar alterações" desabilitado.
   */
  readOnly?: boolean;
  programOptions: JobEditInfoProgramOption[];
  onFieldChange: (field: keyof JobEditInfoValues, value: string) => void;
  onBack: () => void;
  onSubmit: () => void;
}

export function JobEditInfoPage({
  jobName,
  companyLogoUrl,
  companyName,
  values,
  errors,
  isSubmitting,
  readOnly = false,
  programOptions,
  onFieldChange,
  onBack,
  onSubmit,
}: JobEditInfoPageProps) {
  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="jobs" />

      <main className="flex flex-1 flex-col items-center gap-8 bg-card px-4 pt-8 pb-16 md:px-8">
        <form
          className="contents"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <header className="flex w-full max-w-[630px] flex-col gap-2">
            <div className="flex flex-row items-center gap-2">
              <JobHeaderLogo logoUrl={companyLogoUrl} companyName={companyName} />
              <h1 className="text-3xl font-medium leading-none tracking-tight text-foreground md:text-4xl">
                {jobName}
              </h1>
            </div>
            <p className="text-xl font-medium leading-none tracking-tight text-accent-foreground md:text-2xl">
              Informações gerais
            </p>
          </header>

          <div className="flex w-full max-w-[630px] flex-1 flex-col gap-5">
            {/* EU-569 — fieldset nativo desabilita TODO controle de form
                interno de uma vez em modo visualização; o RichTextEditor
                recebe `disabled` explícito (contentEditable não obedece
                fieldset). */}
            <fieldset
              disabled={readOnly}
              className="contents"
              data-testid={readOnly ? 'job-edit-readonly-fieldset' : undefined}
            >
              <BlockedEditTooltip enabled={readOnly}>
                <FormField error={Boolean(errors.name)}>
                  <FormFieldLabel className="text-foreground">Nome da vaga</FormFieldLabel>
                  <Input
                    value={values.name}
                    placeholder="Ex.: Desenvolvimento de softwares"
                    variant={errors.name ? 'destructive' : 'default'}
                    onChange={(event) => onFieldChange('name', event.target.value)}
                    data-testid="job-edit-info-name"
                  />
                  <FormFieldError className="text-sm leading-5 text-muted-foreground">
                    {errors.name}
                  </FormFieldError>
                </FormField>
              </BlockedEditTooltip>

              <BlockedEditTooltip enabled={readOnly}>
                <FormField error={Boolean(errors.programId)}>
                  <FormFieldLabel className="text-foreground">Programa</FormFieldLabel>
                  <NativeSelect
                    value={values.programId}
                    onChange={(event) => onFieldChange('programId', event.target.value)}
                    data-testid="job-edit-info-program"
                  >
                    <option value="" disabled>
                      Selecione o programa
                    </option>
                    {programOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </NativeSelect>
                  <FormFieldError className="text-sm leading-5 text-muted-foreground">
                    {errors.programId}
                  </FormFieldError>
                </FormField>
              </BlockedEditTooltip>

              <BlockedEditTooltip enabled={readOnly}>
                <FormField error={Boolean(errors.description)}>
                  <FormFieldLabel className="text-foreground">Descrição</FormFieldLabel>
                  <RichTextEditor
                    value={values.description}
                    onChange={(html) => onFieldChange('description', html)}
                    disabled={readOnly}
                    data-testid="job-edit-info-description"
                  />
                  <FormFieldError className="text-sm leading-5 text-muted-foreground">
                    {errors.description}
                  </FormFieldError>
                </FormField>
              </BlockedEditTooltip>

              <BlockedEditTooltip enabled={readOnly}>
                <FormField error={Boolean(errors.enrollmentGoal)}>
                  <FormFieldLabel className="text-foreground">Meta de inscritos</FormFieldLabel>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={values.enrollmentGoal}
                    placeholder="Ex.: 500"
                    variant={errors.enrollmentGoal ? 'destructive' : 'default'}
                    onChange={(event) => onFieldChange('enrollmentGoal', event.target.value)}
                    data-testid="job-edit-info-enrollment-goal"
                  />
                  <FormFieldError className="text-sm leading-5 text-muted-foreground">
                    {errors.enrollmentGoal}
                  </FormFieldError>
                </FormField>
              </BlockedEditTooltip>
            </fieldset>

            <div className="mt-auto flex flex-row justify-between gap-2 pt-8">
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={onBack}
                data-testid="job-edit-info-back"
              >
                Voltar
              </Button>
              {readOnly ? (
                <Button type="button" size="lg" disabled data-testid="job-edit-save-blocked">
                  Salvar alterações
                </Button>
              ) : (
                <Button
                  type="submit"
                  size="lg"
                  loading={isSubmitting}
                  data-testid="job-edit-info-submit"
                >
                  Salvar alterações
                </Button>
              )}
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}
